import { useState, useEffect, useCallback } from "react";
import { generateSignedUrl, viewFileInNewTab, downloadFileWithSignedUrl } from "../api/User";

/**
 * Custom hook for managing S3 signed URLs
 * @param {string} s3Url - S3 file URL
 * @param {number} expiresIn - Expiration time in seconds (default: 3600 = 1 hour)
 * @returns {Object} Signed URL state and helper functions
 */
export const useSignedUrl = (s3Url, expiresIn = 3600) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  const token = sessionStorage.getItem("token");

  // Generate signed URL
  const fetchSignedUrl = useCallback(async () => {
    if (!s3Url || !token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await generateSignedUrl(token, s3Url, expiresIn);

      if (response.status === "success") {
        setSignedUrl(response.data.signed_url);
        setExpiresAt(new Date(response.data.expires_at));
      } else {
        throw new Error(response.message || "Failed to generate signed URL");
      }
    } catch (err) {
      console.error("Error fetching signed URL:", err);
      setError(err.message || "Failed to generate signed URL");
    } finally {
      setLoading(false);
    }
  }, [s3Url, token, expiresIn]);

  // Auto-fetch on mount and when URL changes
  useEffect(() => {
    fetchSignedUrl();
  }, [fetchSignedUrl]);

  // Check if URL is expired or about to expire (within 5 minutes)
  const isExpired = useCallback(() => {
    if (!expiresAt) return false;
    const now = new Date();
    const timeUntilExpiry = expiresAt - now;
    return timeUntilExpiry < 5 * 60 * 1000; // 5 minutes buffer
  }, [expiresAt]);

  // Refresh signed URL if expired
  const refreshIfNeeded = useCallback(async () => {
    if (isExpired()) {
      await fetchSignedUrl();
    }
  }, [isExpired, fetchSignedUrl]);

  return {
    signedUrl,
    loading,
    error,
    expiresAt,
    isExpired: isExpired(),
    refresh: fetchSignedUrl,
    refreshIfNeeded,
  };
};

/**
 * Custom hook for viewing files with signed URLs
 * @returns {Object} View and download helper functions
 */
export const useFileViewer = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const token = sessionStorage.getItem("token");

  // View file in new tab
  const viewFile = useCallback(
    async (s3Url, expiresIn = 3600) => {
      if (!s3Url || !token) {
        setError("Missing file URL or authentication token");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        await viewFileInNewTab(token, s3Url, expiresIn);
      } catch (err) {
        console.error("Error viewing file:", err);
        setError(err.message || "Failed to view file");
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  // Download file
  const downloadFile = useCallback(
    async (s3Url, fileName = null, expiresIn = 3600) => {
      if (!s3Url || !token) {
        setError("Missing file URL or authentication token");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        await downloadFileWithSignedUrl(token, s3Url, fileName, expiresIn);
      } catch (err) {
        console.error("Error downloading file:", err);
        setError(err.message || "Failed to download file");
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  return {
    viewFile,
    downloadFile,
    loading,
    error,
    clearError: () => setError(null),
  };
};

/**
 * Custom hook for batch signed URL generation
 * @param {Array<string>} s3Urls - Array of S3 file URLs
 * @param {number} expiresIn - Expiration time in seconds
 * @returns {Object} Signed URLs state and helper functions
 */
export const useBatchSignedUrls = (s3Urls = [], expiresIn = 3600) => {
  const [signedUrls, setSignedUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const token = sessionStorage.getItem("token");

  const fetchBatchSignedUrls = useCallback(async () => {
    if (!s3Urls || s3Urls.length === 0 || !token) return;

    setLoading(true);
    setError(null);

    try {
      const { generateMultipleSignedUrls } = await import("../api/User");
      const response = await generateMultipleSignedUrls(token, s3Urls, expiresIn);

      if (response.status === "success") {
        // Create a mapping of original URL to signed URL
        const urlMap = {};
        response.data.urls.forEach((item) => {
          urlMap[item.original_url] = item.signed_url;
        });
        setSignedUrls(urlMap);
      } else {
        throw new Error(response.message || "Failed to generate signed URLs");
      }
    } catch (err) {
      console.error("Error fetching batch signed URLs:", err);
      setError(err.message || "Failed to generate signed URLs");
    } finally {
      setLoading(false);
    }
  }, [s3Urls, token, expiresIn]);

  useEffect(() => {
    fetchBatchSignedUrls();
  }, [fetchBatchSignedUrls]);

  return {
    signedUrls,
    loading,
    error,
    refresh: fetchBatchSignedUrls,
  };
};
